/**
 * INNFLOW Design System — Topbar
 * Título + subtítulo, InnflowSearch, sino notificação (dot vermelho), h=64px, responsivo
 */

import { useState } from "react";
import { useLocation } from "react-router-dom";

import { InnflowSearch } from "@/components/ui/innflow";
import { NotificationBell } from "@/components/notifications";
import { WhatsAppStatusBadge } from "@/components/layout/WhatsAppStatusBadge";
import { useTotalUnreadCount } from "@/hooks/useUnreadCount";

const routeLabels: Record<string, { title: string; subtitle?: string }> = {
  "/dashboard": { title: "Dashboard", subtitle: "Visão geral" },
  "/dashboard/conversations": { title: "Conversas", subtitle: "Atendimento" },
  "/dashboard/leads": { title: "Leads", subtitle: "Pipeline" },
  "/dashboard/crm": { title: "CRM", subtitle: "Leads e contatos" },
  "/dashboard/kanban": { title: "Kanban", subtitle: "Pipeline visual" },
  "/dashboard/automation": {
    title: "Automação",
    subtitle: "Triggers e fluxos",
  },
  "/dashboard/analytics": { title: "Análises", subtitle: "Métricas" },
  "/dashboard/ai-training": { title: "Treinar IA", subtitle: "Configuração" },
  "/dashboard/settings": {
    title: "Configurações",
    subtitle: "Conta e preferências",
  },
  "/dashboard/billing": { title: "Faturamento", subtitle: "Planos" },
  "/dashboard/reports": { title: "Relatórios", subtitle: "Exportações" },
  "/dashboard/sales": { title: "Vendas", subtitle: "Aprovação e Catálogo" },
  "/dashboard/team": { title: "Equipe", subtitle: "Membros e permissões" },
  "/dashboard/support": { title: "Suporte", subtitle: "Ajuda" },
  "/dashboard/knowledge": {
    title: "Base de Conhecimento",
    subtitle: "Artigos",
  },
};

function getRouteMeta(pathname: string) {
  const exact = routeLabels[pathname];
  if (exact) return exact;
  for (const [path, meta] of Object.entries(routeLabels)) {
    if (pathname.startsWith(path)) return meta;
  }
  return { title: "Dashboard", subtitle: pathname || "Visão geral" };
}

export function Topbar() {
  const location = useLocation();
  const [searchValue, setSearchValue] = useState("");
  const totalUnread = useTotalUnreadCount();
  const { title, subtitle } = getRouteMeta(location.pathname);

  return (
    <header className="sticky top-0 z-30 border-b border-white/[0.05] bg-black/60 backdrop-blur-xl shrink-0">
      <div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
        <div className="flex items-center">
          {/* IA Online indicator */}
          <span
            className="w-2 h-2 rounded-full bg-[#19F58C] animate-pulse mr-3 shrink-0"
            title="IA Online"
          />
          <div>
            <h1 className="text-xl lg:text-[28px] font-extrabold text-white tracking-[-0.02em] truncate">
              {title}
            </h1>
            <p className="text-sm text-white/45 truncate">{subtitle}</p>
          </div>
        </div>

        <div className="flex items-center gap-3 lg:gap-4 shrink-0">
          {/* Search — oculto em mobile */}
          <div className="hidden lg:block w-56 xl:w-64">
            <InnflowSearch
              value={searchValue}
              onChange={setSearchValue}
              placeholder="Buscar..."
              aria-label="Buscar"
            />
          </div>

          <div className="relative flex items-center">
            <WhatsAppStatusBadge />
            <NotificationBell />
            {totalUnread > 0 && (
              <span
                className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse"
                aria-hidden
              />
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
